import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { MessageCircle, MapPin, User, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGoogleReviews } from './GoogleReviewsSection';

const glassStyle = { backgroundColor: 'rgba(255, 255, 255, 0.05)' };

const GlassButton = ({ onClick, icon: Icon, children, primary = false }) => (
  <Button
    onClick={onClick}
    variant={primary ? undefined : 'outline'}
    size="lg"
    className={`px-8 py-6 text-lg font-semibold text-white border-2 backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:shadow-emerald-500/50 shadow-2xl ${
      primary ? 'border-white/20' : 'border-white/40'
    }`}
    style={primary ? { backgroundColor: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(10px)' } : glassStyle}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = primary ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.15)';
      e.currentTarget.style.borderColor = primary ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.6)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = primary ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)';
      e.currentTarget.style.borderColor = primary ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.4)';
    }}
  >
    <Icon className="mr-2" size={20} />
    {children}
  </Button>
);

const GoogleRatingBadge = ({ rating, totalReviews, label }) => (
  <Link
    to="/reviews"
    data-testid="hero-google-rating-badge"
    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/25 backdrop-blur-md transition-all duration-300 hover:scale-105 hover:border-white/50"
    style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
  >
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
    <span className="text-white font-bold text-lg">{rating.toFixed(1)}</span>
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
      ))}
    </div>
    <span className="text-emerald-100/90 text-sm font-medium">{totalReviews} {label}</span>
  </Link>
);

const SEO_KEYWORDS_TEXT = 'Expert Doctor in Chandrapur • General Medicine • Diabetes • Hypertension • Thyroid • Cardiac Care • Respiratory • Preventive Health • Dr. Nikhil Tonge MD';

const HeroBackdrop = () => (
  <>
    {/* Animated background grid */}
    <div className="absolute inset-0 opacity-10">
      <div className="absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />
    </div>
    {/* Glowing orbs */}
    <div className="absolute top-20 left-10 w-64 h-64 bg-emerald-400/20 rounded-full blur-3xl" />
    <div className="absolute bottom-20 right-10 w-80 h-80 bg-teal-400/20 rounded-full blur-3xl" />
  </>
);

const HealthcareHero = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const { data: reviewsData } = useGoogleReviews();

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const reveal = (delay) =>
    `transition-all duration-1000 ${delay} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`;

  return (
    <section className="relative pt-32 pb-24 px-4 overflow-hidden"
      style={{ background: `linear-gradient(135deg, #053827 0%, #064d35 50%, #053827 100%)` }}
    >
      <HeroBackdrop />

      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="text-center mb-12">
          <div className={`flex justify-center mb-8 ${reveal('')}`}>
            <img
              src="/gallery-images/logo.png"
              alt="Tonge Healthcare Logo"
              className="h-32 md:h-40 w-auto object-contain drop-shadow-2xl"
            />
          </div>

          <h1
            className={`text-5xl md:text-7xl font-bold mb-6 text-white tracking-tight ${reveal('delay-200')}`}
            style={{ textShadow: '0 0 40px rgba(16, 185, 129, 0.3)' }}
          >
            {t('hero.title')}
          </h1>

          <p className={`text-2xl md:text-3xl font-semibold mb-4 text-emerald-100 ${reveal('delay-300')}`}>
            {t('hero.tagline')}
          </p>

          <p className={`text-xl md:text-2xl text-emerald-200/80 mb-10 ${reveal('delay-400')}`}>
            {t('hero.subheading')}
          </p>

          <p className={`text-base md:text-lg text-emerald-100/70 mb-10 max-w-4xl mx-auto ${reveal('delay-450')}`}>
            {SEO_KEYWORDS_TEXT}
          </p>

          {reviewsData?.rating && (
            <div className={`flex justify-center mb-8 ${reveal('delay-450')}`}>
              <GoogleRatingBadge
                rating={reviewsData.rating}
                totalReviews={reviewsData.total_reviews}
                label={t('reviews.googleReviews', 'Google Reviews')}
              />
            </div>
          )}

          <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center ${reveal('delay-500')}`}>
            <GlassButton
              primary
              icon={MessageCircle}
              onClick={() => window.open('https://booking.healthray.com/doctor/dr-nikhil-tonge/consultant-physician-and-diabetologist?tid=f71feb0cb689feed974463bf03c12e68ae0ofiAFxyG9McFmAabdGw==', '_blank')}
            >
              {t('hero.bookAppointment')}
            </GlassButton>

            <GlassButton
              icon={User}
              onClick={() => window.open('https://patient.healthray.com/redirect?data=8f02a6a9767c33c80ad643decccc04784bece53621c601e5801bf3cdc07462ae3d0d6f764f85de4abc4652dbae8a181ab3fe4536572164cb', '_blank')}
            >
              {t('hero.patientInfo', 'Patient Info')}
            </GlassButton>

            <GlassButton
              icon={MapPin}
              onClick={() => document.getElementById('location')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('hero.visitUs')}
            </GlassButton>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HealthcareHero;
